// Requests — Internal Requests & Approval Memos (PDF page 14)
import React, { useEffect, useMemo, useState } from 'react';
import { wsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  PageShell, PageHead, Card, Field, Input, Select, Textarea, Tabs, Badge, ReqStatus, RequestRouting, RequestDecision, FileDrop, FileChip, Empty, Loading, Spin, Note,
  useAsync, useProjects, useCostCenters, useCostCategories, useQueryParam, uploadFiles, useSort, Th, money, fmtDate, today,
} from '../../components/ui';
import RequestDetail from '../../components/RequestDetail';
import { notify } from '../../utils/notify';

const SERVICES = [
  { key: 'Internal requisition', icon: 'bi-file-earmark-text', sub: 'Request internal goods or services' },
  { key: 'Expense requisition', icon: 'bi-wallet2', sub: 'Claim project or operational expenses' },
  { key: 'Procurement support', icon: 'bi-cart3', sub: 'Seek help from procurement team' },
  { key: 'HR letter', icon: 'bi-people', sub: 'Request HR letters and certificates' },
];
const REQUEST_TYPES = ['Budget approval', 'Internal requisition', 'Expense requisition', 'Procurement support', 'HR letter', 'General approval'];
const DRAFT_ROUTE = (t) => (t === 'HR letter'
  ? [{ label: 'Reporting manager', description: 'Review' }, { label: 'HR', description: 'Issue letter' }]
  : [{ label: 'Reporting manager', description: 'Review' }, { label: 'Finance', description: 'Budget check' }, { label: 'Authorized approver', description: 'Decision' }]);
const blank = () => ({ subType: 'Budget approval', title: '', project: '', costCenter: '', costCategory: '', amount: '', requiredBy: '', justification: '', paymentMode: 'Bank transfer', payee: '' });

export default function Memos() {
  const { user } = useAuth();
  const projects = useProjects();
  const costCenters = useCostCenters();
  const categories = useCostCategories();
  const openId = useQueryParam('id');
  const [tab, setTab] = useState('new');
  const [f, setF] = useState(blank());
  const [current, setCurrent] = useState(null);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(null);
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState('');
  const mine = useAsync(() => wsAPI.listRequests({ type: 'memo', scope: 'mine' }), []);
  const register = useAsync(() => wsAPI.listRequests({ type: 'memo', scope: 'all' }), []);
  const reloadAll = () => { mine.reload(); register.reload(); };

  // Open ?id= in the memo view
  useEffect(() => {
    if (!openId) return;
    // Requester edits/views in the memo form; approvers get the detail view with actions
    wsAPI.getRequest(openId).then((d) => (d.request.isOwner ? load(d.request) : setDetail(openId))).catch(() => {});
  }, [openId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const load = (r) => {
    setCurrent(r);
    setF({ subType: r.subType || 'Budget approval', title: r.title, project: r.project || '', costCenter: r.costCenter || '', costCategory: r.costCategory || '', amount: r.amount ?? '', requiredBy: r.requiredBy ? r.requiredBy.slice(0, 10) : '', justification: r.data?.justification || '', paymentMode: r.data?.paymentMode || 'Bank transfer', payee: r.data?.payee || '' });
    setFiles([]);
    setTab('new');
  };
  const reset = () => { setCurrent(null); setF(blank()); setFiles([]); };
  const editable = !current || ['DRAFT', 'RETURNED'].includes(current.status);
  const isHR = f.subType === 'HR letter';

  const save = async (submit) => {
    const miss = [];
    if (!f.title.trim()) miss.push('Subject');
    if (submit && !isHR) {
      if (!f.project) miss.push('Project');
      if (!f.costCenter) miss.push('Cost center');
      if (!f.costCategory) miss.push('Cost category');
      if (f.subType !== 'Procurement support' && !(Number(f.amount) > 0)) miss.push('Amount');
    }
    if (submit && !f.requiredBy) miss.push('Required by');
    if (submit && !f.justification.trim()) miss.push('Business justification');
    if (miss.length) { notify.error(`Please fill: ${miss.join(', ')}`); return; }
    setSaving(submit ? 'submit' : 'draft');
    try {
      const attachments = [...(current?.attachments || []), ...(files.length ? await uploadFiles(files, 'workspace') : [])];
      const body = {
        type: 'memo', subType: f.subType, title: f.title, project: f.project, costCenter: f.costCenter, costCategory: f.costCategory,
        amount: f.amount === '' ? undefined : Number(f.amount), requiredBy: f.requiredBy || undefined, attachments,
        data: { justification: f.justification, paymentMode: f.paymentMode, payee: f.payee }, submit,
      };
      const r = current ? await wsAPI.updateRequest(current._id, body) : await wsAPI.createRequest(body);
      setCurrent(r.request);
      setFiles([]);
      notify.success(submit ? `${r.request.refNo} sent for approval` : `Draft saved — ${r.request.refNo}`);
      reloadAll();
    } catch (e) { notify.error(e.message || 'Could not save'); } finally { setSaving(null); }
  };

  const catOptions = categories.filter((c) => c.status === 'Active').map((c) => c.name);
  const rowsAll = (tab === 'mine' ? mine.data?.requests : register.data?.requests) || [];
  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    return !t ? rowsAll : rowsAll.filter((r) => [r.refNo, r.title, r.requesterName, r.project, r.subType].some((v) => String(v || '').toLowerCase().includes(t)));
  }, [rowsAll, q]);
  const sort = useSort(rows, 'updatedAt', 'desc');
  const awaiting = (register.data?.requests || []).filter((r) => r.canAct).length;

  return (
    <PageShell section="Requests">
      <PageHead crumbs={[{ label: 'Home', to: '/dashboard' }, { label: 'Requests', to: '/requests/my' }, { label: 'Internal Requests & Approval Memos' }]} title="Internal Requests & Approval Memos" />
      <div className="tse-row mb-18" style={{ justifyContent: 'space-between' }}>
        <Tabs variant="boxed" value={tab} onChange={setTab} tabs={[{ key: 'mine', label: 'My requests' }, { key: 'new', label: current ? `Memo ${current.refNo}` : 'New request' }, { key: 'register', label: `Memo register${awaiting ? ` (${awaiting} to act)` : ''}` }]} />
        {tab === 'new' && current && <button type="button" className="tse-btn sm outline" onClick={reset}><i className="bi bi-plus-lg" />New memo</button>}
      </div>

      {tab === 'new' ? (
        <div className="tse-grid" style={{ gridTemplateColumns: 'minmax(260px, 0.8fr) minmax(0, 1.55fr) minmax(300px, 1fr)', alignItems: 'start' }}>
          <Card title="Request a service" bodyClass="tight">
            <div className="tse-stack" style={{ gap: 8 }}>
              {SERVICES.map((s) => (
                <button key={s.key} type="button" className={`tse-tile ${f.subType === s.key ? 'outlined' : ''}`} disabled={!editable} onClick={() => set('subType', s.key)}>
                  <i className={`bi ${s.icon}`} style={{ fontSize: '1.404rem', color: '#1479bd' }} />
                  <div><div className="t">{s.key}</div><div className="s">{s.sub}</div></div>
                  <i className="bi bi-chevron-right chev" />
                </button>
              ))}
            </div>
          </Card>

          <div className="tse-stack" style={{ gap: 8 }}>
            <Card title={<span style={{ fontSize: '1.053rem' }}>Approval memo</span>} actions={<Badge tone="gray" lg>Status: {current ? (current.status === 'PENDING' ? 'Pending' : current.status.charAt(0) + current.status.slice(1).toLowerCase()) : 'Draft'}</Badge>}>
              <div className="tse-form-grid c2">
                <Field label="Memo ID"><Input value={current?.refNo || 'Assigned on save'} readOnly /></Field>
                <Field label="Request type"><Select value={f.subType} onChange={(e) => set('subType', e.target.value)} options={REQUEST_TYPES} disabled={!editable} /></Field>
                <Field label="Subject" required><Input value={f.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Site office setup" disabled={!editable} /></Field>
                <Field label="Project" required={!isHR}><Select value={f.project} onChange={(e) => set('project', e.target.value)} placeholder="Select project" options={projects.map((p) => p.projectCode)} disabled={!editable} /></Field>
                <Field label="Cost center" required={!isHR}><Select value={f.costCenter} onChange={(e) => set('costCenter', e.target.value)} placeholder="Select cost center" options={[...new Set([...(f.costCenter ? [f.costCenter] : []), ...costCenters.filter((c) => c.status === 'Active').map((c) => c.code)])]} disabled={!editable} /></Field>
                <Field label="Cost category" required={!isHR}><Select value={f.costCategory} onChange={(e) => set('costCategory', e.target.value)} placeholder="Select category" options={[...new Set([...(f.costCategory ? [f.costCategory] : []), ...catOptions])]} disabled={!editable} /></Field>
                <Field label="Amount" required={!isHR && f.subType !== 'Procurement support'}><div className="tse-input-group"><span className="pre">₹</span><Input type="number" min="0" value={f.amount} onChange={(e) => set('amount', e.target.value)} disabled={!editable} /></div></Field>
                <Field label="Required by" required><Input type="date" min={today()} value={f.requiredBy} onChange={(e) => set('requiredBy', e.target.value)} disabled={!editable} /></Field>
                {['Expense requisition', 'Budget approval'].includes(f.subType) && (
                  <>
                    <Field label="Payment mode"><Select value={f.paymentMode} onChange={(e) => set('paymentMode', e.target.value)} options={['Bank transfer', 'Cash', 'Vendor payment']} disabled={!editable} /></Field>
                    <Field label="Payee (if any)"><Input value={f.payee} onChange={(e) => set('payee', e.target.value)} placeholder="Vendor / employee" disabled={!editable} /></Field>
                  </>
                )}
                <Field label="Business justification" required className="span-all">
                  <Textarea rows={3} maxLength={500} value={f.justification} onChange={(e) => set('justification', e.target.value)} disabled={!editable} />
                  <div className="tse-counter">{f.justification.length}/500</div>
                </Field>
                <div className="span-all">
                  <div className="tse-label" style={{ marginBottom: 6 }}>Attachments</div>
                  <div className="tse-grid g-2">
                    {(current?.attachments || []).map((a, i) => <FileChip key={a.s3Key || i} file={a} />)}
                    {files.map((file, i) => <FileChip key={`n${i}`} file={file} onRemove={() => setFiles((p) => p.filter((_, j) => j !== i))} />)}
                    {editable && <FileDrop inline icon="bi-upload" title="Add more files" sub="PDF, DOC, XLS, PNG (Max 10 MB)" onFiles={(fs) => setFiles((p) => [...p, ...fs])} />}
                  </div>
                </div>
              </div>
            </Card>
            {editable && (
              <div className="tse-card"><div className="tse-card-body tight tse-row">
                <button type="button" className="tse-btn" onClick={() => save(true)} disabled={!!saving}>{saving === 'submit' ? <Spin /> : <i className="bi bi-send" />}Send for approval</button>
                <span className="tse-small tse-muted">Submit the memo to start the approval workflow.</span>
                <div className="tse-spacer" />
                <button type="button" className="tse-btn outline sm" onClick={() => save(false)} disabled={!!saving}>{saving === 'draft' ? <Spin /> : null}Save draft</button>
              </div></div>
            )}
            {current?.status === 'RETURNED' && <Note tone="warn" title="Returned for clarification">{(current.history || []).filter((h) => h.action === 'RETURNED').slice(-1)[0]?.remarks} — update and send again.</Note>}
          </div>

          <div className="tse-stack" style={{ gap: 8 }}>
            <Card title="Approval routing">
              {current && current.status !== 'DRAFT' ? <RequestRouting request={current} /> : (
                <RequestRouting request={{ status: 'DRAFT', requesterName: user?.userName, stages: DRAFT_ROUTE(f.subType).map((s) => ({ ...s, status: 'WAITING' })) }} />
              )}
            </Card>
            <Card title="Approver actions">
              {current?.canAct ? <RequestDecision request={current} onDone={(r) => { setCurrent(r); reloadAll(); }} /> : (
                <Note tone="plain">{current && current.status === 'PENDING' ? `Waiting for ${current.pendingWith || 'approver'}.` : current?.status === 'APPROVED' ? `Approved on ${fmtDate(current.completedAt)}.` : 'Actions appear here for the current approver.'}</Note>
              )}
            </Card>
          </div>
        </div>
      ) : (
        <Card bodyClass="flush">
          <div className="tse-toolbar"><div className="tse-search"><i className="bi bi-search" /><input placeholder="Search memos" value={q} onChange={(e) => setQ(e.target.value)} /></div></div>
          {(tab === 'mine' ? mine.loading : register.loading) ? <Loading /> : !rows.length ? <Empty icon="bi-file-earmark-check" title="No memos yet" /> : (
            <div className="tse-table-wrap"><table className="tse-table">
              <thead><tr><Th sort={sort} k="refNo">Memo ID</Th><Th sort={sort} k="title">Subject</Th><th>Type</th><th>Project</th><Th sort={sort} k="amount">Amount</Th><th>Requested by</th><th>Status</th><Th sort={sort} k="updatedAt">Updated</Th><th /></tr></thead>
              <tbody>{sort.rows.map((r) => (
                <tr key={r._id} className="clickable" onClick={() => load(r)} style={r.canAct ? { background: '#fffaf0' } : undefined}>
                  <td className="strong">{r.refNo}</td><td>{r.title}</td><td>{r.subType}</td><td>{r.project || '—'}</td><td>{r.amount ? money(r.amount) : '—'}</td><td>{r.requesterName}</td>
                  <td><ReqStatus request={r} />{r.canAct && <div className="tse-small" style={{ color: '#b7791f', marginTop: 3 }}>Awaiting your action</div>}</td><td>{fmtDate(r.updatedAt)}</td>
                  <td onClick={(e) => e.stopPropagation()}><button type="button" className="tse-btn xs outline" onClick={() => setDetail(r._id)}>Details</button></td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </Card>
      )}
      <RequestDetail id={detail} onClose={() => setDetail(null)} onChanged={reloadAll} />
    </PageShell>
  );
}
